import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import Footer from "@/components/layout/Footer";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, forgotPassword } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return; // guard against a second submit firing before disabled re-renders
    setLoginError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success(`Karibu, ${res.user.name || res.user.email}!`);
      navigate(res.user.must_change_password ? "/change-password" : "/dashboard", { replace: true });
    } else {
      const message = res.error || "Login failed — please check your email & password";
      setLoginError(message);
      toast.error(message);
    }
  };

  const handleForgot = async () => {
    if (!forgotEmail) return toast.error("Please enter your email");
    const r = await forgotPassword(forgotEmail);
    if (r.ok) {
      toast.success("If that email exists, a reset link was sent. Check your inbox or contact your admin.");
      setForgotOpen(false);
      setForgotEmail("");
    } else {
      toast.error(r.error || "Could not send reset email");
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="login-bg" style={{ backgroundImage: `url(${process.env.PUBLIC_URL || ""}/img/login-bg.jpg)` }} />
      <div className="login-overlay" />

      <div className="relative z-10 min-h-screen flex flex-col">
      <div className="flex-1 flex flex-col lg:flex-row">
        {/* Left brand panel */}
        <div className="flex-1 hidden lg:flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl overflow-hidden shadow-2xl shadow-primary/30 shrink-0">
              <img src="/img/karibunet-logo.jpg" alt="KaribuNet" className="w-full h-full object-cover" />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="font-display text-lg font-semibold tracking-tight">KARIS</span>
              <span className="text-[10px] uppercase tracking-[0.24em] text-white/70">KaribuNet Limited</span>
            </div>
          </div>

          <div className="max-w-lg">
            <p className="text-xs uppercase tracking-[0.32em] text-amber-300 mb-4 font-semibold">Karibu Zanzibar</p>
            <h1 className="font-display text-5xl xl:text-6xl font-semibold leading-[1.05] tracking-tight">
              KaribuNet Administration,
              <span className="block mt-1">
                <span className="kn-gradient-text">Resources &amp; Integrated System</span>
              </span>
            </h1>
            <p className="mt-6 text-white/75 text-base leading-relaxed max-w-md">
              ERP · CRM · HRM · Accounting · Field Service — one platform, fully integrated, built for Tanzania&apos;s most ambitious teams.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {["Sales", "Accounting", "Inventory", "HR", "Service", "Reports"].map((t) => (
                <span key={t} className="text-xs px-3 py-1.5 rounded-full border border-white/20 bg-white/5 backdrop-blur-md text-white/90">{t}</span>
              ))}
            </div>
          </div>

          <div className="text-xs text-white/50">© 2026 KaribuNet Limited · Kwa Mchina Mwanzo, Zanzibar</div>
        </div>

        {/* Right login card */}
        <div className="w-full lg:w-[480px] flex items-center justify-center p-6 lg:p-10">
          <Card className="w-full glass-strong border-white/15 dark:border-white/10 p-8 shadow-2xl shadow-black/40 rounded-2xl">
            <div className="lg:hidden flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl overflow-hidden shrink-0">
                <img src="/img/karibunet-logo.jpg" alt="KaribuNet" className="w-full h-full object-cover" />
              </div>
              <span className="font-display text-lg font-semibold">KARIS</span>
            </div>

            <h2 className="font-display text-3xl font-semibold tracking-tight">Welcome back</h2>
            <p className="text-sm text-muted-foreground mt-1">Sign in to your KaribuNet workspace</p>

            <form onSubmit={submit} className="mt-7 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs uppercase tracking-[0.18em]">Email</Label>
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setLoginError(""); }}
                  required
                  autoComplete="email"
                  disabled={loading}
                  className="h-11"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-xs uppercase tracking-[0.18em]">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setLoginError(""); }}
                    required
                    autoComplete="current-password"
                    disabled={loading}
                    className="h-11 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    data-testid="toggle-password-visibility"
                    disabled={loading}
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {loginError && (
                <div
                  data-testid="login-error-banner"
                  className="text-sm text-rose-600 dark:text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2.5"
                >
                  {loginError}
                </div>
              )}

              <div className="flex items-center justify-between text-xs">
                <button
                  type="button"
                  onClick={() => setForgotOpen(true)}
                  className="text-primary hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>

              <Button
                type="submit"
                data-testid="login-submit-button"
                disabled={loading}
                className="w-full h-11 kn-gradient-bg text-white border-0 hover:opacity-95 font-medium"
              >
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing in…</> : "Sign in"}
              </Button>
            </form>

            {forgotOpen && (
              <div className="mt-5 p-4 rounded-xl border border-border bg-muted/30 space-y-3">
                <Label className="text-xs uppercase tracking-[0.18em]">Reset email</Label>
                <Input
                  data-testid="forgot-email-input"
                  type="email"
                  placeholder="you@karibunet.com"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                />
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setForgotOpen(false)}>Cancel</Button>
                  <Button size="sm" onClick={handleForgot} data-testid="forgot-submit-button">Send link</Button>
                </div>
              </div>
            )}

            <p className="mt-8 text-[11px] text-muted-foreground text-center">
              Need access? Contact your KaribuNet administrator.
            </p>
          </Card>
        </div>
      </div>
      <Footer variant="dark" />
      </div>
    </div>
  );
}
